import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Create axios instance with default config.
// Exported so non-domain modules (eventLog, InsightsAccessContext) can
// share the auth-token interceptor + base URL instead of using bare
// axios (which doesn't carry the Authorization header).
export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000, // 10 seconds
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth readiness gate.
//
// The token reaches localStorage asynchronously: Auth0 -> TokenSync's effect ->
// getAccessTokenSilently() -> localStorage. The interceptor used to read
// localStorage synchronously and simply omit the Authorization header when the
// token wasn't there yet, so any request fired during that window came back 401
// (most visibly GET /tenders/bookmarks?scope=team on a hard refresh). Requests
// now wait for the token instead of silently going out unauthenticated.
const DEV_MODE = process.env.REACT_APP_DEV_MODE === 'true';
const AUTH_READY_TIMEOUT_MS = 5000;

let resolveAuthReady;
let authReady = new Promise((resolve) => { resolveAuthReady = resolve; });
// Dev mode bypasses Auth0 entirely, so there is never a token to wait for.
if (DEV_MODE) resolveAuthReady();

// Called by TokenSync once a token is stored, and also when the user is
// confirmed logged out — either way the wait is over.
export const markAuthReady = () => { if (resolveAuthReady) resolveAuthReady(); };
// Re-arm the gate on logout so the next login waits properly again.
export const resetAuthReady = () => {
  if (DEV_MODE) return;
  authReady = new Promise((resolve) => { resolveAuthReady = resolve; });
};

// Request interceptor for adding auth tokens if needed
api.interceptors.request.use(
  async (config) => {
    let token = localStorage.getItem('authToken');
    if (!token) {
      // Bounded wait — never hang a request if auth never resolves.
      await Promise.race([
        authReady,
        new Promise((resolve) => setTimeout(resolve, AUTH_READY_TIMEOUT_MS)),
      ]);
      token = localStorage.getItem('authToken');
    }
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// API endpoints
export const tendersAPI = {
  // Get recent tenders
  getRecentTenders: async (params = {}) => {
    const response = await api.get('/tenders/recent', { params });
    return response.data;
  },

  // Get all tenders with filters (deprecated - use searchTenders instead)
  getTenders: async (params = {}) => {
    const response = await api.get('/tenders', { params });
    return response.data;
  },

  // Get tender by ID
  getTender: async (tenderId, language = 'eng') => {
    // Use query parameter endpoint if tender ID contains special characters
    if (tenderId.includes('/') || tenderId.includes('%')) {
      const response = await api.get(`/tenders/tender-by-id`, {
        params: { id: tenderId, language }
      });
      return response.data;
    } else {
      const response = await api.get(`/tenders/${tenderId}`, {
        params: { language }
      });
      return response.data;
    }
  },

  // Get filter options
  getFilterOptions: async (language = 'eng') => {
    const response = await api.get('/tenders/filters/options', {
      params: { language }
    });
    return response.data;
  },

  // Find similar tenders
  getSimilarTenders: async (tenderId, size = 5, language = 'eng') => {
    // Use query parameter endpoint if tender ID contains special characters
    if (tenderId.includes('/') || tenderId.includes('%')) {
      const response = await api.post(`/tenders/similar-by-id`, {}, {
        params: { id: tenderId, size, language }
      });
      return response.data;
    } else {
      const response = await api.post(`/tenders/${tenderId}/similar`, {}, {
        params: { size, language }
      });
      return response.data;
    }
  },


  // Semantic search tenders (using Search Service)
  semanticSearchTenders: async (searchParams) => {
    const {
      query,
      status,
      category,
      region,
      organization,
      procurement_method,
      notice_type,
      from_date,
      to_date,
      language = 'eng',
      page = 1,
      size = 20,
      filters = {}
    } = searchParams;

    // Sprint 04-30 P2: same filter-building shape as searchTenders so
    // Dashboard's filters (categories multi-select, both date dimensions,
    // BI fields) actually apply in semantic mode too. Previously this
    // dropped everything except a handful of legacy top-level args.
    const searchRequest = {
      query: query || '',
      filters: {
        status: filters.status || (status ? [status] : undefined),
        categories: filters.categories || filters.category || (category ? [category] : undefined),
        regions: filters.regions || (region ? [region] : undefined),
        organizations: filters.organizations || (organization ? [organization] : undefined),
        date_from: filters.from_date || from_date,
        date_to: filters.to_date || to_date,
        from_date: filters.from_date || from_date,
        to_date: filters.to_date || to_date,
        closing_date_from: filters.closing_date_from || searchParams.closing_date_from,
        closing_date_to: filters.closing_date_to || searchParams.closing_date_to,
        has_pdfs: filters.has_pdfs === true || filters.has_pdfs === 'true' ? true : undefined,
        // Source system (CanadaBuys vs municipal portals). This object is an
        // allow-list rebuilt from scratch, not a pass-through — a filter the
        // caller sets is silently dropped unless it is named here.
        sources: filters.sources,
      },
      page,
      size
    };

    // Remove undefined values from filters
    Object.keys(searchRequest.filters).forEach(key => {
      if (searchRequest.filters[key] === undefined || searchRequest.filters[key] === null ||
          (Array.isArray(searchRequest.filters[key]) && searchRequest.filters[key].length === 0)) {
        delete searchRequest.filters[key];
      }
    });

    // Call Search Service semantic search endpoint
    const response = await axios.post('/api/search/semantic', searchRequest, {
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    return {
      total: response.data.total_hits,
      tenders: response.data.hits.map(hit => ({
        id: hit.id,
        tender_id: hit.id,
        title: hit.title,
        description: hit.description,
        organization: hit.organization,
        amount: hit.amount,
        publish_date: hit.date,
        closing_date: hit.closing_date,
        status: hit.status,
        categories: hit.categories,
        regions: hit.regions,
        relevance_score: hit.score,
        highlights: hit.highlights
      })),
      page: response.data.page,
      size: response.data.size,
      max_score: response.data.hits[0]?.score || 0
    };
  },

  // Keyword-only search (strict matching, no fuzziness - best for acronyms like PPE, IT)
  keywordSearchTenders: async (searchParams) => {
    const {
      query,
      status,
      category,
      region,
      organization,
      from_date,
      to_date,
      language = 'eng',
      page = 1,
      size = 20,
      filters = {}
    } = searchParams;

    // Prepare search request for Search Service keyword endpoint
    const searchRequest = {
      query: query || '',
      // Sprint 04-30 P2: parity with searchTenders so categories / both
      // date dimensions / BI filters compose with keyword mode too.
      filters: {
        status: filters.status || (status ? [status] : undefined),
        categories: filters.categories || filters.category || (category ? [category] : undefined),
        regions: filters.regions || (region ? [region] : undefined),
        organizations: filters.organizations || (organization ? [organization] : undefined),
        date_from: filters.from_date || from_date,
        date_to: filters.to_date || to_date,
        from_date: filters.from_date || from_date,
        to_date: filters.to_date || to_date,
        closing_date_from: filters.closing_date_from || searchParams.closing_date_from,
        closing_date_to: filters.closing_date_to || searchParams.closing_date_to,
        has_pdfs: filters.has_pdfs === true || filters.has_pdfs === 'true' ? true : undefined,
        // Source system (CanadaBuys vs municipal portals). This object is an
        // allow-list rebuilt from scratch, not a pass-through — a filter the
        // caller sets is silently dropped unless it is named here.
        sources: filters.sources,
      },
      page,
      size
    };

    // Remove undefined values from filters
    Object.keys(searchRequest.filters).forEach(key => {
      if (searchRequest.filters[key] === undefined || searchRequest.filters[key] === null ||
          (Array.isArray(searchRequest.filters[key]) && searchRequest.filters[key].length === 0)) {
        delete searchRequest.filters[key];
      }
    });

    // Call Search Service keyword search endpoint (strict matching)
    const response = await axios.post('/api/search/keyword', searchRequest, {
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return {
      total: response.data.total_hits,
      tenders: response.data.hits.map(hit => ({
        id: hit.id,
        tender_id: hit.id,
        reference_number: hit.reference_number,
        solicitation_number: hit.solicitation_number,
        title: hit.title,
        description: hit.description,
        organization: hit.organization,
        amount: hit.amount,
        publish_date: hit.date,
        closing_date: hit.closing_date,
        status: hit.status,
        categories: hit.categories,
        regions: hit.regions,
        relevance_score: hit.score,
        highlights: hit.highlights
      })),
      page: response.data.page,
      size: response.data.size,
      max_score: response.data.hits[0]?.score || 0
    };
  },

  // Get real-time statistics
  getStats: async (language = 'eng') => {
    const response = await api.get('/tenders/stats', {
      params: { language }
    });
    return response.data;
  },

  // PDF-related endpoints
  getTenderPdfs: async (tenderId) => {
    const response = await api.get(`/api/tender/${encodeURIComponent(tenderId)}/pdfs`);
    return response.data;
  },

  getRecentPdfs: async (limit = 50) => {
    const response = await api.get('/api/pdfs/recent', {
      params: { limit }
    });
    return response.data;
  },

  getPdfStats: async () => {
    const response = await api.get('/api/pdfs/stats');
    return response.data;
  },

  // Updated searchTenders to use Search Service hybrid search
  searchTenders: async (searchParams) => {
    
    const {
      search,
      query,
      status,
      category,
      region,
      organization,
      procurement_method,
      notice_type,
      from_date,
      to_date,
      only_open_bids,
      language = 'eng',
      page = 1,
      size = 20,
      sort_by = 'relevance',
      sort_order = 'desc',
      filters = {}
    } = searchParams;

    // Prepare search request for Search Service
    const searchRequest = {
      query: search || query || '',
      filters: {
        status: filters.status || (status ? [status] : undefined),
        // Sprint 04-30 P2: Dashboard sends `filters.categories` (plural,
        // multi-select). Old callers pass `filters.category` (singular) or
        // a top-level `category` arg, kept for back-compat.
        categories: filters.categories || filters.category || (category ? [category] : undefined),
        regions: filters.region || (region ? [region] : undefined),
        organizations: filters.organization || (organization ? [organization] : undefined),
        date_from: filters.from_date || from_date,
        date_to: filters.to_date || to_date,
        closing_date_from: filters.closing_date_from || searchParams.closing_date_from,
        closing_date_to: filters.closing_date_to || searchParams.closing_date_to,
        // Content filters
        has_pdfs: filters.has_pdfs === true || filters.has_pdfs === 'true' ? true : undefined,
        // Source system (CanadaBuys vs municipal portals). This object is an
        // allow-list rebuilt from scratch, not a pass-through — a filter the
        // caller sets is silently dropped unless it is named here.
        sources: filters.sources,
        // Business Intelligence filters - use actual field names
        pdfProcessingStatus: filters.pdfProcessingStatus,
        "canadianMade-fabriquéCanadien": filters['canadianMade-fabriquéCanadien'],
        "experienceRequired-expérienceRequise": filters['experienceRequired-expérienceRequise'] === 'true' ? true : filters['experienceRequired-expérienceRequise'] === 'false' ? false : undefined,
        "indigenousBusinessRequired-entrepriseAutochtooneRequise": filters['indigenousBusinessRequired-entrepriseAutochtooneRequise'] === 'true' ? true : filters['indigenousBusinessRequired-entrepriseAutochtooneRequise'] === 'false' ? false : undefined,
        "masterCategory-catégoriePrincipale": filters['masterCategory-catégoriePrincipale']
      },
      page,
      size,
      sort_by: sort_by === 'publication_date' ? 'date' : sort_by
    };

    // Remove undefined values from filters
    Object.keys(searchRequest.filters).forEach(key => {
      if (searchRequest.filters[key] === undefined || searchRequest.filters[key] === null || 
          (Array.isArray(searchRequest.filters[key]) && searchRequest.filters[key].length === 0)) {
        delete searchRequest.filters[key];
      }
    });

    // Call Search Service hybrid search endpoint through API gateway
    const response = await axios.post('/api/search', searchRequest, {
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return {
      total: response.data.total_hits,
      tenders: response.data.hits.map(hit => ({
        id: hit.id,
        tender_id: hit.id,
        reference_number: hit.reference_number,
        solicitation_number: hit.solicitation_number,
        title: hit.title,
        description: hit.description,
        organization: hit.organization,
        amount: hit.amount,
        publish_date: hit.date,
        closing_date: hit.closing_date,
        status: hit.status,
        categories: hit.categories,
        // Sprint 04-30 P2: search backend returns the listing-row Category
        // (Goods / Services / Construction) inside `categories[0]`. Surface
        // it as `procurementCategory` so the Dashboard card's existing
        // fallback chain picks it up without needing array handling.
        procurementCategory: (Array.isArray(hit.categories) && hit.categories[0]) || null,
        regions: hit.regions,
        relevance_score: hit.score,
        highlights: hit.highlights,
        // Business Intelligence fields
        pdfProcessingStatus: hit.pdfProcessingStatus,
        'canadianMade-fabriquéCanadien': hit['canadianMade-fabriquéCanadien'],
        'experienceRequired-expérienceRequise': hit['experienceRequired-expérienceRequise'],
        'indigenousBusinessRequired-entrepriseAutochtooneRequise': hit['indigenousBusinessRequired-entrepriseAutochtooneRequise'],
        'masterCategory-catégoriePrincipale': hit['masterCategory-catégoriePrincipale'],
        'productCertifications-certificationsProducts': hit['productCertifications-certificationsProducts'],
        'businessCertifications-certificationsAffaires': hit['businessCertifications-certificationsAffaires'],
        'deliveryRegions-régionsLivraison': hit['deliveryRegions-régionsLivraison'],
        'contractLengthYears-duréeContratAnnées': hit['contractLengthYears-duréeContratAnnées']
      })),
      page: response.data.page,
      size: response.data.size,
      aggregations: response.data.aggregations,
      max_score: response.data.hits[0]?.score || 0
    };
  },
  // Calculate bid likelihood/acceptance score (fast endpoint)
  calculateBidLikelihood: async (tenderId) => {
    // Use query parameter endpoint if tender ID contains special characters
    if (tenderId.includes('/') || tenderId.includes('%')) {
      const response = await api.get(`/api/agentic/bid-likelihood-by-id`, {
        params: { id: tenderId }
      });
      return response.data;
    } else {
      const response = await api.get(`/api/agentic/bid-likelihood/${tenderId}`);
      return response.data;
    }
  }
};

// New Agentic RAG API endpoints
export const agenticAPI = {
  // Process natural language queries
  processQuery: async (query, userProfile = null, conversationId = null) => {
    const response = await api.post('/api/agentic/query', {
      query,
      user_profile: userProfile,
      conversation_id: conversationId
    });
    return response.data;
  },

  // Intelligent search with natural language
  intelligentSearch: async (query, userProfile = null, filters = null) => {
    const params = new URLSearchParams({ query });
    const response = await api.post('/api/agentic/search', {
      query,
      user_profile: userProfile,
      filters
    });
    return response.data;
  },

  // Analyze specific tender
  analyzeTender: async (tenderId, userProfile = null) => {
    // Use query parameter endpoint if tender ID contains special characters
    if (tenderId.includes('/') || tenderId.includes('%')) {
      const response = await api.post(`/api/agentic/analyze-tender-by-id`, 
        userProfile ? { user_profile: userProfile } : {},
        { params: { id: tenderId } }
      );
      return response.data;
    } else {
      const response = await api.post(`/api/agentic/analyze-tender/${tenderId}`, 
        userProfile ? { user_profile: userProfile } : {}
      );
      return response.data;
    }
  },

  // Get personalized recommendations
  getRecommendations: async (userProfile, filters = null) => {
    const response = await api.post('/api/agentic/recommendations', {
      user_profile: userProfile,
      filters
    });
    return response.data;
  },

  // Batch analyze multiple tenders
  batchAnalyze: async (tenderIds, userProfile = null) => {
    const response = await api.post('/api/agentic/batch-analyze', {
      tender_ids: tenderIds,
      user_profile: userProfile
    });
    return response.data;
  },

  // Get conversation history
  getConversationHistory: async (conversationId) => {
    const response = await api.get(`/api/agentic/conversation/${conversationId}`);
    return response.data;
  },

  // List available agents
  listAgents: async () => {
    const response = await api.get('/api/agentic/agents');
    return response.data;
  },

  // Get query examples
  getExamples: async () => {
    const response = await api.get('/api/agentic/examples');
    return response.data;
  },

  // Health check for agentic system
  healthCheck: async () => {
    const response = await api.get('/api/agentic/health');
    return response.data;
  }
};





// PDF Document API endpoints
export const pdfAPI = {
  // Get all PDFs for a tender
  getTenderPDFs: async (tenderId) => {
    const response = await api.get(`/api/tender/${encodeURIComponent(tenderId)}/pdfs`);
    return response.data;
  },

  // Get direct PDF view URL
  getPDFViewUrl: (tenderId, filename) => {
    return `${API_BASE_URL}/api/tender/${encodeURIComponent(tenderId)}/pdf/${encodeURIComponent(filename)}`;
  },

  // Get recent PDFs across all tenders
  getRecentPDFs: async (limit = 50) => {
    const response = await api.get('/api/pdfs/recent', { params: { limit } });
    return response.data;
  },

  // Get PDF processing statistics
  getPDFStats: async () => {
    const response = await api.get('/api/pdfs/stats');
    return response.data;
  },

  // Upload one or more documents for a tender. Accepts a single File or
  // an array of Files. Backend always returns the aggregate shape:
  //   { success, total, succeeded_count, failed_count, uploaded[], failed[] }
  uploadDocument: async (tenderId, fileOrFiles, platformName = 'User Upload') => {
    const files = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles];
    const formData = new FormData();
    for (const f of files) formData.append('files', f);
    formData.append('tender_id', tenderId);
    formData.append('platform_name', platformName);

    const response = await axios.post('/api/documents/upload', formData, {
      // Scale timeout with file count — 2 minutes per file, capped at 10 minutes
      timeout: Math.min(10 * 60 * 1000, Math.max(120000, files.length * 60_000)),
      headers: { 'Content-Type': 'multipart/form-data' },
      // Accept 207 (partial success) as a non-error response
      validateStatus: (s) => s === 200 || s === 207,
    });
    return response.data;
  }
};

// Pipeline Metrics API endpoints
export const metricsAPI = {
  // Get pipeline metrics from the metrics index
  getMetrics: async () => {
    const response = await axios.get('/api/metrics', {
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  }
};

// Disqualifications API (Sprint 04-30 P1: team-shared rejected tenders).
// Mirrors the bookmarks endpoints but lives at /api/tenders/disqualifications
// and returns the full team-wide list on GET (DQ is shared, not per-user).
export const disqualificationsAPI = {
  list: async () => {
    const r = await api.get('/tenders/disqualifications', { timeout: 30000 });
    return r.data;
  },
  disqualify: async (tender) => {
    const r = await api.post('/tenders/disqualifications', {
      tender_id: tender.tender_id || tender.id,
      title: tender.title,
      organization: typeof tender.organization === 'object'
        ? (tender.organization?.contracting_entity || tender.organization?.name || 'Government of Canada')
        : tender.organization,
      closing_date: tender.closing_date || tender.closingDate,
      reason: tender.reason || null,
    });
    return r.data;
  },
  remove: async (tenderId) => {
    const r = await api.delete(`/tenders/disqualifications/${encodeURIComponent(tenderId)}`);
    return r.data;
  },
};

// Bookmarks API endpoints (backend-synced saved tenders)
export const bookmarksAPI = {
  // Get all bookmarks for authenticated user
  getBookmarks: async (scope) => {
    // Team scope returns the whole org's saves (deduped + enriched) — allow more
    // than the default 10s.
    const response = await api.get('/tenders/bookmarks',
      scope ? { params: { scope }, timeout: 30000 } : undefined);
    return response.data;
  },

  // Get the whole team's saved tenders (everyone incl. the caller, deduped with
  // saved_by) — the unified team-wide Saved list (team decision).
  getTeamBookmarks: async () => {
    const response = await api.get('/tenders/bookmarks', { params: { scope: 'team' } });
    return response.data;
  },

  // Add a bookmark
  addBookmark: async (bookmark) => {
    const response = await api.post('/tenders/bookmarks', {
      tender_id: bookmark.tender_id || bookmark.id,
      title: bookmark.title,
      organization: typeof bookmark.organization === 'object'
        ? (bookmark.organization?.contracting_entity || bookmark.organization?.name || 'Government of Canada')
        : bookmark.organization,
      status: bookmark.status,
      closing_date: bookmark.closing_date || bookmark.closingDate,
      notes: bookmark.notes,
      pipeline_stage: bookmark.pipeline_stage || bookmark.pipelineStage || 'reviewing'
    });
    return response.data;
  },

  // Remove a bookmark
  removeBookmark: async (tenderId) => {
    const response = await api.delete(`/tenders/bookmarks/${encodeURIComponent(tenderId)}`);
    return response.data;
  },

  // Update bookmark fields (notes, pipeline_stage, analysis_status, etc.)
  updateBookmark: async (tenderId, updates) => {
    // Normalize camelCase → snake_case for backend
    const payload = {};
    if (updates.notes !== undefined) payload.notes = updates.notes;
    if (updates.pipeline_stage !== undefined) payload.pipeline_stage = updates.pipeline_stage;
    if (updates.pipelineStage !== undefined) payload.pipeline_stage = updates.pipelineStage;
    if (updates.analysis_status !== undefined) payload.analysis_status = updates.analysis_status;
    if (updates.analysisStatus !== undefined) payload.analysis_status = updates.analysisStatus;
    if (updates.analyzed_at !== undefined) payload.analyzed_at = updates.analyzed_at;
    if (updates.analyzedAt !== undefined) payload.analyzed_at = updates.analyzedAt;
    if (updates.bid_recommendation !== undefined) payload.bid_recommendation = updates.bid_recommendation;
    if (updates.bidRecommendation !== undefined) payload.bid_recommendation = updates.bidRecommendation;

    const response = await api.patch(`/tenders/bookmarks/${encodeURIComponent(tenderId)}`, payload);
    return response.data;
  },

  // Export bookmarks as JSON
  exportBookmarks: async () => {
    const response = await api.get('/tenders/bookmarks/export', {
      responseType: 'blob'
    });
    return response.data;
  },

  // Import bookmarks from JSON
  importBookmarks: async (bookmarks) => {
    const response = await api.post('/tenders/bookmarks/import', {
      bookmarks: bookmarks
    });
    return response.data;
  },

  // Sync local bookmarks with backend (for migration from localStorage)
  syncBookmarks: async (localBookmarks) => {
    const response = await api.post('/tenders/bookmarks/sync', {
      bookmarks: localBookmarks
    });
    return response.data;
  },

  // Bidirectional reconciliation — replaces one-time sync
  reconcile: async (localBookmarks) => {
    const response = await api.post('/tenders/bookmarks/reconcile', {
      bookmarks: localBookmarks.map(b => ({
        tender_id: b.tender_id || b.id,
        title: b.title,
        organization: typeof b.organization === 'object'
          ? (b.organization?.contracting_entity || b.organization?.name || 'Government of Canada')
          : b.organization,
        status: b.status,
        closing_date: b.closing_date || b.closingDate,
        notes: b.notes,
        pipeline_stage: b.pipeline_stage || b.pipelineStage,
        analysis_status: b.analysis_status || b.analysisStatus,
        analyzed_at: b.analyzed_at || b.analyzedAt,
        bid_recommendation: b.bid_recommendation || b.bidRecommendation,
        bookmarked_at: b.bookmarked_at || b.bookmarkedAt
      }))
    }, { timeout: 60000 });
    return response.data;
  }
};

// Pricing Intelligence API (Elastic Agent Builder)
export const pricingIntelligenceAPI = {
  // Ask a free-form pricing question
  ask: async (question, sessionId = null) => {
    const response = await axios.post('/api/ai/pricing/ask', {
      question,
      session_id: sessionId
    }, {
      timeout: 180000, // 3 minutes - Agent Builder can take time
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  },

  // Get pricing benchmark for a GSIN code
  getBenchmark: async (gsinCode) => {
    const response = await axios.post('/api/ai/pricing/benchmark', {
      gsin_code: gsinCode
    }, {
      timeout: 180000,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  },

  // Find similar contracts
  getSimilarContracts: async (gsinCode, limit = 5) => {
    const response = await axios.post('/api/ai/pricing/similar-contracts', {
      gsin_code: gsinCode,
      limit
    }, {
      timeout: 180000,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  },

  // Get competitor analysis for a supplier
  getCompetitorAnalysis: async (supplierName) => {
    const response = await axios.post('/api/ai/pricing/competitor-analysis', {
      supplier_name: supplierName
    }, {
      timeout: 180000,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  },

  // Get buyer analysis for an organization
  getBuyerAnalysis: async (buyerOrg) => {
    const response = await axios.post('/api/ai/pricing/buyer-analysis', {
      buyer_org: buyerOrg
    }, {
      timeout: 180000,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  },

  // Get bid recommendation
  getBidRecommendation: async (gsinCode, estimatedValue, buyerOrg) => {
    const response = await axios.post('/api/ai/pricing/bid-recommendation', {
      gsin_code: gsinCode,
      estimated_value: estimatedValue,
      buyer_org: buyerOrg
    }, {
      timeout: 180000,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  },

  // Run full analysis pipeline for a tender
  runFullAnalysis: async (tender) => {
    const gsinCode = tender.procurementCategory ||
                     tender['masterCategory-catégoriePrincipale'] ||
                     tender.procurement_category ||
                     'Unknown';
    const buyerOrg = tender.organization?.contracting_entity ||
                     tender.organization?.name ||
                     'Government of Canada';
    const estimatedValue = tender.amount || tender.value?.amount || 100000;

    // Run analyses in parallel where possible
    const [benchmark, similarContracts, buyerAnalysis, recommendation] = await Promise.all([
      pricingIntelligenceAPI.getBenchmark(gsinCode).catch(e => ({ error: e.message })),
      pricingIntelligenceAPI.getSimilarContracts(gsinCode, 5).catch(e => ({ error: e.message })),
      pricingIntelligenceAPI.getBuyerAnalysis(buyerOrg).catch(e => ({ error: e.message })),
      pricingIntelligenceAPI.getBidRecommendation(gsinCode, estimatedValue, buyerOrg).catch(e => ({ error: e.message }))
    ]);

    return {
      benchmark,
      similarContracts,
      buyerAnalysis,
      recommendation,
      tender: {
        gsinCode,
        buyerOrg,
        estimatedValue
      }
    };
  }
};

// Reference Pricing API (browse reference-pricing ES index)
export const referencePricingAPI = {
  list: async (params = {}) => {
    const response = await axios.get('/api/bid-agents/reference-pricing', {
      params,
      timeout: 10000,
    });
    return response.data;
  },

  getStats: async () => {
    const response = await axios.get('/api/bid-agents/reference-pricing/stats', {
      timeout: 10000,
    });
    return response.data;
  },

  getLinkedTenders: async (docId) => {
    const response = await axios.get(`/api/bid-agents/reference-pricing/${encodeURIComponent(docId)}/tenders`, {
      timeout: 15000,
    });
    return response.data;
  },

  getBenchmarks: async (gsinCode, category) => {
    const response = await axios.get('/api/bid-agents/reference-pricing/benchmarks', {
      params: { gsin: gsinCode || '', category: category || '' },
      timeout: 10000,
    });
    return response.data;
  },

  getContracts: async (keywords, category, limit = 5) => {
    const response = await axios.get('/api/bid-agents/reference-pricing/contracts', {
      params: { q: keywords || '', category: category || '', limit },
      timeout: 10000,
    });
    return response.data;
  },
};

// Bid Agents API (Multi-agent bid analysis with Claude via AWS Bedrock)
export const bidAgentsAPI = {
  /**
   * Batch restriction screen for the Recommended page.
   * Checks structured BI fields + Phase 1 cache against our blocker rules.
   * Returns per-tender { blocked, reasons, canadian_content_flag, cache_hit }.
   * @param {Array} tenders - Array of tender objects from search results
   * @returns {Promise<{ results: Object }>}
   */
  screenTenders: async (tenders) => {
    const payload = {
      tenders: tenders.map((t) => ({
        id: t._id || t.tender_id || t.reference_number,
        indigenous_required: t['indigenousBusinessRequired-entrepriseAutochtooneRequise'] || null,
        security_clearance_required: t.securityClearanceRequired || t.security_clearance_required || null,
        canadian_made: t['canadianMade-fabriquéCanadien'] || null,
      })),
    };
    const response = await axios.post('/api/bid-agents/screen', payload, {
      timeout: 15000,
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  },

  /**
   * Start a new bid analysis for a tender.
   * @param {Object} request - Analysis request
   * @param {string} request.tender_id - The tender ID to analyze
   * @param {string} [request.tender_title] - Title of the tender
   * @param {string} [request.gsin_code] - GSIN code for historical lookup
   * @param {string} [request.buyer_org] - Buyer organization name
   * @param {Array} [request.pdf_urls] - PDF URLs to extract from
   * @param {string} [request.pdf_text] - Pre-extracted PDF text
   * @param {Array} [request.products] - Pre-extracted products array
   * @returns {Promise<{job_id: string, status: string, message: string}>}
   */
  startAnalysis: async (request) => {
    const response = await axios.post('/api/bid-agents/analyze', request, {
      timeout: 120000, // 120 seconds - event loop may be busy if another analysis is running
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  },

  /**
   * Get real-time status of an analysis job.
   * Use for polling during analysis.
   * @param {string} jobId - The job ID to check
   * @returns {Promise<Object>} Job status with progress, phase, tool executions
   */
  getJobStatus: async (jobId) => {
    const response = await axios.get(`/api/bid-agents/status/${jobId}`, {
      timeout: 10000,
    });
    return response.data;
  },

  /**
   * Get full results of a completed analysis.
   * @param {string} jobId - The job ID to get results for
   * @returns {Promise<Object>} Full analysis results
   */
  getJobResult: async (jobId) => {
    const response = await axios.get(`/api/bid-agents/result/${jobId}`, {
      timeout: 10000,
    });
    return response.data;
  },

  /**
   * Get all analyses for a specific tender from history.
   * @param {string} tenderId - The tender ID
   * @param {number} [limit=10] - Maximum results
   * @returns {Promise<{tender_id: string, analyses: Array}>}
   */
  getTenderAnalyses: async (tenderId, limit = 10) => {
    const response = await axios.get(`/api/bid-agents/history/tender/${encodeURIComponent(tenderId)}`, {
      params: { limit },
      timeout: 10000,
    });
    return response.data;
  },

  /**
   * Get recent analyses across all tenders.
   * @param {number} [limit=20] - Maximum results
   * @param {string} [status] - Filter by status (completed, failed)
   * @returns {Promise<{analyses: Array, total: number}>}
   */
  getRecentAnalyses: async (limit = 20, status = null) => {
    const params = { limit };
    if (status) params.status = status;

    const response = await axios.get('/api/bid-agents/history/recent', {
      params,
      timeout: 10000,
    });
    return response.data;
  },

  /**
   * Get a specific stored analysis by job ID.
   * @param {string} jobId - The job ID
   * @returns {Promise<{source: string, analysis: Object}>}
   */
  getStoredAnalysis: async (jobId) => {
    const response = await axios.get(`/api/bid-agents/history/${jobId}`, {
      timeout: 10000,
    });
    return response.data;
  },

  /**
   * Delete a stored analysis.
   * @param {string} jobId - The job ID to delete
   * @returns {Promise<{message: string}>}
   */
  deleteAnalysis: async (jobId) => {
    const response = await axios.delete(`/api/bid-agents/history/${jobId}`, {
      timeout: 10000,
    });
    return response.data;
  },

  /**
   * Run a specific phase for a tender.
   * Phase endpoints: POST /phase/{0-4}/{tender_id}
   * @param {number} phaseNum - Phase number (0-4)
   * @param {string} tenderId - The tender ID
   * @param {boolean} [force=false] - Bypass cache
   * @returns {Promise<Object>} Phase result
   */
  runPhase: async (phaseNum, tenderId, force = false) => {
    const params = force ? { force: true } : {};
    const response = await axios.post(
      `/api/bid-agents/phase/${phaseNum}/${encodeURIComponent(tenderId)}`,
      null,
      {
        params,
        timeout: 300000,
      },
    );
    return response.data;
  },

  /**
   * Get all cached phase results for a tender.
   * @param {string} tenderId - The tender ID
   * @returns {Promise<{tender_id: string, phases: Object}>}
   */
  getTenderResults: async (tenderId) => {
    try {
      const response = await axios.get(`/api/bid-agents/results/${encodeURIComponent(tenderId)}`, {
        timeout: 10000,
      });
      return response.data;
    } catch (err) {
      // A 404 means status says "complete" but no aggregated result is stored
      // (cache expired / ran elsewhere). That's expected — return null instead
      // of throwing, so callers don't spam the console for every tender.
      if (err?.response?.status === 404) return null;
      throw err;
    }
  },

  /**
   * Invalidate cache for a tender (forces re-analysis).
   * @param {string} tenderId - The tender ID
   * @returns {Promise<{tender_id: string, deleted: number}>}
   */
  invalidateCache: async (tenderId) => {
    const response = await axios.delete(`/api/bid-agents/cache/${encodeURIComponent(tenderId)}`, {
      timeout: 10000,
    });
    return response.data;
  },

  /**
   * Quick RAG summary — fast AI overview of a tender (<10 seconds).
   * @param {string} tenderId - The tender ID
   * @returns {Promise<{summary: string, products_identified: Array, key_requirements: Array, confidence: string}>}
   */
  quickSummary: async (tenderId) => {
    const response = await axios.post(
      `/api/bid-agents/quick-summary/${encodeURIComponent(tenderId)}`,
      null,
      { timeout: 30000 },
    );
    return response.data;
  },

  /**
   * Export a PDF report of all bid analysis results.
   * @param {string} tenderId - The tender ID
   * @returns {Promise<Blob>} PDF file as blob
   */
  exportReport: async (tenderId) => {
    const response = await axios.get(
      `/api/bid-agents/report/${encodeURIComponent(tenderId)}`,
      { responseType: 'blob', timeout: 30000 },
    );
    return response.data;
  },

  /**
   * Health check for bid agents service.
   * @returns {Promise<Object>} Health status
   */
  healthCheck: async () => {
    const response = await axios.get('/api/bid-agents/health', {
      timeout: 5000,
    });
    return response.data;
  },

  /**
   * Poll for analysis completion with callback.
   * @param {string} jobId - The job ID to poll
   * @param {Function} onProgress - Callback for progress updates
   * @param {number} [intervalMs=2000] - Polling interval in ms
   * @param {number} [timeoutMs=300000] - Maximum wait time (5 min default)
   * @returns {Promise<Object>} Final result when completed
   */
  pollUntilComplete: async (jobId, onProgress, intervalMs = 2000, timeoutMs = 300000) => {
    const startTime = Date.now();

    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          if (Date.now() - startTime > timeoutMs) {
            reject(new Error('Analysis timed out'));
            return;
          }

          const status = await bidAgentsAPI.getJobStatus(jobId);

          if (onProgress) {
            onProgress(status);
          }

          if (status.status === 'completed') {
            const result = await bidAgentsAPI.getJobResult(jobId);
            resolve(result);
          } else if (status.status === 'failed') {
            reject(new Error(status.error || 'Analysis failed'));
          } else {
            setTimeout(poll, intervalMs);
          }
        } catch (error) {
          reject(error);
        }
      };

      poll();
    });
  },

  /**
   * Run full analysis and poll until complete.
   * Convenience method combining startAnalysis + pollUntilComplete.
   * @param {Object} request - Analysis request
   * @param {Function} onProgress - Progress callback
   * @returns {Promise<Object>} Final analysis result
   */
  runFullAnalysis: async (request, onProgress) => {
    const { job_id } = await bidAgentsAPI.startAnalysis(request);
    return bidAgentsAPI.pollUntilComplete(job_id, onProgress);
  },

  // ============================================================================
  // Fire-and-forget analysis + polling (persistent in ES)
  // ============================================================================

  /**
   * Start a background analysis that persists across navigation.
   * @param {string} tenderId - The tender ID to analyze
   * @returns {Promise<{tender_id: string, status: string}>}
   */
  startAnalysisBackground: async (tenderId) => {
    const response = await axios.post(
      `/api/bid-agents/analyze/${encodeURIComponent(tenderId)}`,
      null,
      { timeout: 30000 },
    );
    return response.data;
  },

  /**
   * Poll analysis status (ES-persisted, survives navigation/refresh).
   * @param {string} tenderId - The tender ID
   * @returns {Promise<{tender_id, status, completed_phases, running_phases, failed_phases, recent_progress, started_at, completed_at, error}>}
   */
  getAnalysisStatus: async (tenderId) => {
    try {
      const response = await axios.get(
        `/api/bid-agents/analyze/status/${encodeURIComponent(tenderId)}`,
        { timeout: 10000 },
      );
      return response.data;
    } catch (err) {
      // No job / endpoint hiccup is expected when initializing 100s of
      // bookmarked tenders at once — treat as idle instead of throwing, so
      // the console isn't flooded with non-actionable errors.
      return { tender_id: tenderId, status: 'idle' };
    }
  },

  // ============================================================================
  // Qualification API (Auto-triggers on tender view — runs Phase 0 + 1)
  // ============================================================================

  /**
   * Start qualification analysis for a tender.
   * Runs automatically - no user interaction required.
   * Answers: Can we bid? Will we be eliminated? Can we make money?
   * @param {string} tenderId - Elasticsearch tender ID
   * @param {string} [tenderUrl] - Optional URL for live validation
   * @returns {Promise<{job_id: string, status: string, tender_id: string}>}
   */
  startQualification: async (tenderId, tenderUrl = null) => {
    const params = tenderUrl ? { tender_url: tenderUrl } : {};
    const response = await axios.post(`/api/bid-agents/qualify/${encodeURIComponent(tenderId)}`, null, {
      params,
      timeout: 60000,
    });
    return response.data;
  },

  /**
   * Get qualification status/results.
   * @param {string} jobId - The qualification job ID
   * @returns {Promise<Object>} Qualification result with can_bid, restrictions, products
   */
  getQualificationStatus: async (jobId) => {
    const response = await axios.get(`/api/bid-agents/qualify/${jobId}/status`, {
      timeout: 10000,
    });
    return response.data;
  },

  /**
   * Poll qualification until complete.
   * @param {string} jobId - Job ID to poll
   * @param {Function} onProgress - Progress callback
   * @param {number} [intervalMs=1000] - Polling interval (1s default for quick ops)
   * @param {number} [timeoutMs=60000] - Max wait (60s - should be quick)
   * @returns {Promise<Object>} Final qualification result
   */
  pollQualification: async (jobId, onProgress, intervalMs = 1000, timeoutMs = 60000) => {
    const startTime = Date.now();

    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          if (Date.now() - startTime > timeoutMs) {
            reject(new Error('Qualification timed out'));
            return;
          }

          const status = await bidAgentsAPI.getQualificationStatus(jobId);

          if (onProgress) {
            onProgress(status);
          }

          if (status.status === 'completed') {
            resolve(status.qualification_result);
          } else if (status.status === 'failed') {
            reject(new Error(status.error || 'Qualification failed'));
          } else {
            setTimeout(poll, intervalMs);
          }
        } catch (error) {
          reject(error);
        }
      };

      poll();
    });
  },

  /**
   * Run full qualification and poll until complete.
   * Call this when tender detail page loads.
   * @param {string} tenderId - Tender ID
   * @param {string} [tenderUrl] - Optional tender URL
   * @param {Function} [onProgress] - Progress callback
   * @returns {Promise<Object>} Final qualification result
   */
  runQualification: async (tenderId, tenderUrl = null, onProgress = null) => {
    const { job_id } = await bidAgentsAPI.startQualification(tenderId, tenderUrl);
    return bidAgentsAPI.pollQualification(job_id, onProgress);
  },

  /**
   * Get the daily pipeline report (goods/supplies tenders, cached 24h).
   * First call may take 15-30s; subsequent calls return instantly from cache.
   * @returns {Promise<{report_title, narrative, columns, rows[], total_hits, query_params, generated_at}>}
   */
  getPipelineReport: async () => {
    const response = await axios.get('/api/bid-agents/pipeline-report', {
      timeout: 120000,
    });
    return response.data;
  },

  /**
   * Force-refresh the daily pipeline report (delete cache, re-generate).
   * @returns {Promise<{report_title, narrative, columns, rows[], total_hits, query_params, generated_at}>}
   */
  refreshPipelineReport: async () => {
    const response = await axios.post('/api/bid-agents/pipeline-report/refresh', null, {
      timeout: 120000,
    });
    return response.data;
  },

  /**
   * Run an Elastic Agent Report (sync fallback).
   * @param {string} question - The user's natural-language question
   * @returns {Promise<{report_title, narrative, columns, rows[], total_hits, query_params, generated_at}>}
   */
  runElasticReport: async (question) => {
    const response = await axios.post('/api/bid-agents/elastic-report',
      { question },
      { timeout: 120000 },
    );
    return response.data;
  },

  /**
   * Stream an Elastic Agent Report via SSE.
   * @param {string} question - The user's question
   * @param {Object} callbacks - Event callbacks
   * @param {Function} callbacks.onStatus - ({message}) status updates
   * @param {Function} callbacks.onToolCall - ({tool}) agent tool usage
   * @param {Function} callbacks.onMessageChunk - ({content}) streaming text
   * @param {Function} callbacks.onMessageComplete - ({content}) full agent response
   * @param {Function} callbacks.onReportComplete - (report) final structured report
   * @param {Function} callbacks.onError - ({message}) error
   * @returns {Promise<void>}
   */
  streamElasticReport: async (question, callbacks = {}) => {
    const response = await fetch('/api/bid-agents/elastic-report/stream', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ question }),
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || `HTTP ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Parse SSE events from buffer
      const parts = buffer.split('\n\n');
      buffer = parts.pop(); // keep incomplete event in buffer

      for (const part of parts) {
        if (!part.trim()) continue;
        let eventType = '';
        let eventData = '';
        for (const line of part.split('\n')) {
          if (line.startsWith('event: ')) eventType = line.slice(7).trim();
          else if (line.startsWith('data: ')) eventData = line.slice(6);
        }
        if (!eventType || !eventData) continue;

        try {
          const data = JSON.parse(eventData);
          const cb = callbacks[`on${eventType.charAt(0).toUpperCase()}${eventType.slice(1).replace(/_([a-z])/g, (_, c) => c.toUpperCase())}`];
          if (cb) cb(data);
        } catch (e) {
          console.warn('SSE parse error:', e, eventData);
        }
      }
    }
  },

  /**
   * Send a chat message about a tender (grounded in analysis + PDF content).
   * @param {string} tenderId - The tender ID
   * @param {string} message - The user's message
   * @param {Array<{role: string, content: string}>} history - Prior messages
   * @returns {Promise<{reply: string}>}
   */
  sendChatMessage: async (tenderId, message, history = []) => {
    const response = await axios.post(
      `/api/bid-agents/chat/${encodeURIComponent(tenderId)}`,
      { message, history },
      { timeout: 60000 },
    );
    return response.data;
  },
};

export default api;
// ---------------------------------------------------------------------------
// FORGE — sourcing & bid-assembly stage (forge service, /api/forge/*)
// ---------------------------------------------------------------------------
const enc = (s) => encodeURIComponent(s);

export const forgeAPI = {
  // Workspace
  listTenders: async () => (await api.get('/forge/forge-tenders')).data,
  archiveTender: async (id) => (await api.post(`/forge/forge/${enc(id)}/archive`)).data,
  unarchiveTender: async (id) => (await api.post(`/forge/forge/${enc(id)}/unarchive`)).data,
  getSummary: async (tenderId) =>
    (await api.get(`/forge/forge/${enc(tenderId)}/summary`)).data,

  // Send a tender to FORGE (async): spec sheet + bid requirements generate in the
  // background; the tender appears in the cockpit immediately as "preparing".
  enterForge: async (tenderId, title = '') =>
    (await api.post(`/forge/forge/${enc(tenderId)}/enter`, { title })).data,
  // Remove a tender from FORGE (deletes its spec + sourcing state; stays in Discovery).
  removeFromForge: async (tenderId) =>
    (await api.delete(`/forge/forge/${enc(tenderId)}`)).data,

  // FORGE Operator Agent — the conversational driver (grounded chat; S0 read-only).
  getAgentChat: async (tenderId) =>
    (await api.get(`/forge/forge/${enc(tenderId)}/agent`)).data,
  sendAgentChat: async (tenderId, text) =>
    (await api.post(`/forge/forge/${enc(tenderId)}/agent/message`, { text }, { timeout: 120000 })).data,
  // Execute a confirmed / card-clicked agent action (e.g. Award from a quote card).
  agentAction: async (tenderId, action, params) =>
    (await api.post(`/forge/forge/${enc(tenderId)}/agent/action`, { action, params }, { timeout: 60000 })).data,
  // Derived live Accio status for the cockpit status strip (safe to poll).
  accioStatus: async (tenderId) =>
    (await api.get(`/forge/forge/${enc(tenderId)}/accio-status`)).data,

  // Proactive sourcing (P0): momentum/coverage roll-up + per-tender autopilot pause.
  momentum: async (tenderId) =>
    (await api.get(`/forge/forge/${enc(tenderId)}/momentum`)).data,
  pauseSourcing: async (tenderId, paused) =>
    (await api.post(`/forge/forge/${enc(tenderId)}/pause`, { paused })).data,

  // Product designs — author/upload our OWN specs and source them through FORGE.
  listProducts: async (q = '') =>
    (await api.get(`/forge/product${q ? `?q=${encodeURIComponent(q)}` : ''}`)).data,
  createProduct: async (payload) =>
    (await api.post('/forge/product', payload)).data,
  getProduct: async (id) => (await api.get(`/forge/product/${enc(id)}`)).data,
  updateProduct: async (id, updates) =>
    (await api.patch(`/forge/product/${enc(id)}`, updates)).data,
  deleteProduct: async (id) => (await api.delete(`/forge/product/${enc(id)}`)).data,
  addProductLine: async (id, line) =>
    (await api.post(`/forge/product/${enc(id)}/line-item`, line)).data,
  patchProductLine: async (id, lineItem, updates) =>
    (await api.patch(`/forge/product/${enc(id)}/line-item/${lineItem}`, updates)).data,
  deleteProductLine: async (id, lineItem) =>
    (await api.delete(`/forge/product/${enc(id)}/line-item/${lineItem}`)).data,
  // Upload a spec PDF; the backend extracts line items (Claude). Slow — allow headroom.
  uploadProductSpec: async (id, file, replace = false) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post(
      `/forge/product/${enc(id)}/upload-spec?replace=${replace ? 'true' : 'false'}`,
      formData, { timeout: 180000, headers: { 'Content-Type': 'multipart/form-data' } });
    return res.data;
  },
  sendProductToSourcing: async (id) =>
    (await api.post(`/forge/product/${enc(id)}/send-to-sourcing`)).data,
  // Describe -> craft: LLM builds the product's line items from a description (+ attachments).
  craftProduct: async (id, description) =>
    (await api.post(`/forge/product/${enc(id)}/craft`, { description }, { timeout: 180000 })).data,
  // Attach a reference image to a product (rides into sourcing via spec-images).
  uploadProductImage: async (id, file, lineItem = 0) => {
    const fd = new FormData(); fd.append('file', file);
    const res = await api.post(`/forge/product/${enc(id)}/image?line_item=${lineItem}`, fd,
      { timeout: 120000, headers: { 'Content-Type': 'multipart/form-data' } });
    return res.data;
  },
  listProductImages: async (id) => (await api.get(`/forge/product/${enc(id)}/images`)).data,
  productImageUrl: (id, imgId) => `${API_BASE_URL}/forge/spec-image/${enc(id)}/${enc(imgId)}`,

  // Spec sheet
  getSpecSheet: async (tenderId) =>
    (await api.get(`/forge/spec-sheet/${enc(tenderId)}`)).data,
  generateSpecSheet: async (tenderId, force = false) =>
    (await api.post(`/forge/spec-sheet/${enc(tenderId)}/generate`, { force }, { timeout: 120000 })).data,
  patchLineItem: async (tenderId, lineItem, updates) =>
    (await api.patch(`/forge/spec-sheet/${enc(tenderId)}/line-item`, { line_item: lineItem, updates })).data,
  setQuoteQuantity: async (tenderId, lineItem, quoteQuantities) =>
    (await api.post(`/forge/spec-sheet/${enc(tenderId)}/quote-quantity`, { line_item: lineItem, quote_quantities: quoteQuantities })).data,
  chatSpecSheet: async (tenderId, message, history = []) =>
    (await api.post(`/forge/spec-sheet/${enc(tenderId)}/chat`, { message, history }, { timeout: 120000 })).data,
  specSheetExportUrl: (tenderId, lineItem = null) =>
    `${API_BASE_URL}/forge/spec-sheet/${enc(tenderId)}/export`
    + (lineItem != null ? `?line_item=${lineItem}` : ''),

  // Shortlist / candidates
  // Images / design figures extracted from the tender's source PDFs (slow ~PDF fetch).
  getSpecImages: async (tenderId) =>
    (await api.get(`/forge/spec-sheet/${enc(tenderId)}/images`, { timeout: 120000 })).data,
  getShortlist: async (tenderId) =>
    (await api.get(`/forge/shortlist/${enc(tenderId)}`)).data,
  addCandidate: async (tenderId, payload) =>
    (await api.post(`/forge/shortlist/${enc(tenderId)}/candidate`, payload)).data,
  // Auto-discover manufacturers (Apify Alibaba -> website/contact scrape).
  // Slow (~30-60s/product); override the default 10s axios timeout (nginx
  // allows 300s on /api/forge/).
  discover: async (tenderId, productName, limit = 8) =>
    (await api.post(
      `/forge/shortlist/${enc(tenderId)}/discover?product_name=${encodeURIComponent(productName)}&limit=${limit}`,
      {}, { timeout: 240000 }
    )).data,
  setCandidateStatus: async (candidateId, status) =>
    (await api.post(`/forge/candidate/${enc(candidateId)}/status`, { status })).data,
  importVendors: async (tenderId, csv, defaultProduct = '') =>
    (await api.post(`/forge/shortlist/${enc(tenderId)}/import-vendors`,
      { csv, default_product: defaultProduct })).data,
  prepareBid: async (tenderId, force = false) =>
    (await api.post(`/forge/forge/${enc(tenderId)}/prepare?force=${force}`, {},
      { timeout: 120000 })).data,

  // Supplier registry (cross-tender Suppliers console)
  listSuppliers: async (query = '', favoriteOnly = false, tag = '') =>
    (await api.get(`/forge/suppliers`, { params: { query, favorite_only: favoriteOnly, tag } })).data,
  getSupplier: async (supplierId) =>
    (await api.get(`/forge/suppliers/${enc(supplierId)}`)).data,
  upsertSupplier: async (payload) =>
    (await api.post(`/forge/suppliers`, payload)).data,
  favoriteSupplier: async (supplierId, favorite = true) =>
    (await api.post(`/forge/suppliers/${enc(supplierId)}/favorite?favorite=${favorite}`)).data,
  patchSupplier: async (supplierId, payload) =>
    (await api.patch(`/forge/suppliers/${enc(supplierId)}`, payload)).data,

  // Comms
  getInbox: async (tenderId) =>
    (await api.get(`/forge/inbox/${enc(tenderId)}`)).data,
  getThreadMessages: async (threadId) =>
    (await api.get(`/forge/thread/${enc(threadId)}/messages`)).data,
  sendMessage: async (threadId, body, channel = null, agentAction = null) =>
    (await api.post(`/forge/thread/${enc(threadId)}/message`, { body, channel, agent_action: agentAction })).data,
  openConversation: async (candidateId) =>
    (await api.post(`/forge/candidate/${enc(candidateId)}/open-conversation`, {}, { timeout: 120000 })).data,
  nextTurn: async (threadId) =>
    (await api.post(`/forge/thread/${enc(threadId)}/next-turn`, {}, { timeout: 120000 })).data,
  escalate: async (threadId, reason = 'analyst review') =>
    (await api.post(`/forge/thread/${enc(threadId)}/escalate`, {}, { params: { reason } })).data,
  // Autonomous loop tick — ingest supplier replies + let the agent respond.
  pollSourcing: async (tenderId) =>
    (await api.post(`/forge/sourcing/${enc(tenderId)}/poll`, {}, { timeout: 180000 })).data,
  // Adversarial supplier simulator (end-to-end conversation testing).
  simReply: async (threadId, persona = 'standard') =>
    (await api.post(`/forge/sim/thread/${enc(threadId)}/reply`, {}, { params: { persona }, timeout: 120000 })).data,
  simRun: async (threadId, turns = 3, persona = 'standard') =>
    (await api.post(`/forge/sim/thread/${enc(threadId)}/run`, {}, { params: { turns, persona }, timeout: 300000 })).data,

  // Quotes
  getQuotes: async (tenderId) =>
    (await api.get(`/forge/quotes/${enc(tenderId)}`)).data,
  getComparison: async (tenderId) =>
    (await api.get(`/forge/quotes/${enc(tenderId)}/comparison`)).data,
  intakeQuote: async (tenderId, payload) =>
    (await api.post(`/forge/quotes/${enc(tenderId)}/intake`, payload)).data,
  selectQuote: async (tenderId, quoteId) =>
    (await api.post(`/forge/quotes/${enc(tenderId)}/select`, { quote_id: quoteId })).data,
  quotesByLineItem: async (tenderId) =>
    (await api.get(`/forge/quotes/${enc(tenderId)}/by-line-item`)).data,
  awardLineItem: async (tenderId, lineItem, quoteId) =>
    (await api.post(`/forge/quotes/${enc(tenderId)}/award`, { line_item: lineItem, quote_id: quoteId })).data,
  clearAward: async (tenderId, lineItem) =>
    (await api.delete(`/forge/quotes/${enc(tenderId)}/award/${lineItem}`)).data,
  getBidSummary: async (tenderId) =>
    (await api.get(`/forge/forge/${enc(tenderId)}/bid-summary`)).data,
  markAwarded: async (tenderId) =>
    (await api.post(`/forge/forge/${enc(tenderId)}/mark-awarded`)).data,

  // Operator <-> Accio orchestration chat (bridged over Telegram). Agent messages
  // are sanitized server-side; send relays to the bridge to reach the agent.
  getAccioChat: async (tenderId = '') =>
    (await api.get(`/forge/accio-chat`, { params: tenderId ? { tender_id: tenderId } : {} })).data,
  sendAccioChat: async (text, tenderId = '') =>
    (await api.post(`/forge/accio-chat/send`, { text, tender_id: tenderId })).data,

  // Supplier evidence store — key files Accio pulled from Alibaba (certs, spec
  // sheets, quote PDFs, product images), linked to a shortlist candidate.
  getSupplierDocuments: async (tenderId) =>
    (await api.get(`/forge/supplier-documents/${enc(tenderId)}`)).data,
  // Resolves to a short-lived presigned S3 URL ({url}); open it to view/download.
  getSupplierDocUrl: async (docId) =>
    (await api.get(`/forge/supplier-document/${enc(docId)}/download`)).data?.url,

  // Bid requirements (how to bid)
  getBidRequirements: async (tenderId, refresh = false) =>
    (await api.get(`/forge/bid-requirements/${enc(tenderId)}`, { params: { refresh }, timeout: 120000 })).data,

  // Documents — original tender docs + working documents
  getDocuments: async (tenderId) =>
    (await api.get(`/forge/documents/${enc(tenderId)}`)).data,
  uploadWorkingDoc: async (tenderId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post(`/forge/documents/${enc(tenderId)}/working`, formData, {
      timeout: 120000,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  },
  deleteWorkingDoc: async (tenderId, docId) =>
    (await api.delete(`/forge/documents/${enc(tenderId)}/working/${enc(docId)}`)).data,
  workingDocUrl: (tenderId, docId) =>
    `${API_BASE_URL}/forge/documents/${enc(tenderId)}/working/${enc(docId)}/download`,

  // Validation + freight
  getValidation: async (tenderId) =>
    (await api.get(`/forge/validation/${enc(tenderId)}`)).data,
  freightEmail: async (tenderId, packing = {}) =>
    (await api.post(`/forge/freight/${enc(tenderId)}/quote-email`, packing)).data,
};
